import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { SectorService } from '../services/sector.service';
import { ToastService } from 'services/toast.service';

import { DEFAULT_POST_CLIENT, getCreateClient } from '../constants/client.constants';
import { DEFAULT_POST_SITE, getCreateSite } from '../constants/site.constants';
import { DEFAULT_POST_SECTOR } from '../constants/sector.constants';
import { ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, CommonGlobalService, MultiSelect, Option, RootState, Site, UserFilter, UserLinked } from '@umanforge/common';
import { PostSector } from 'interfaces/api_sector.interfaces';
import { PostClient } from 'interfaces/api_client.interfaces';
import { PostSite } from 'interfaces/api_site.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { ClientsIcon } from '../components/icons/clients_icon.component';
import { UsersIcon } from '../components/icons/users_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';
import { SectorsIcon } from '../components/icons/sectors_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';

const toastService: ToastService = new ToastService( 'Create Sector' );


export function CreateSectorView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const createNewClient: Client = getCreateClient( t );
  const createNewSite: Site = getCreateSite( t );

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ postSector, setPostSector ] = useState<PostSector>( structuredClone( DEFAULT_POST_SECTOR ) );
  const [ postSite, setPostSite ] = useState<PostSite>( structuredClone( DEFAULT_POST_SITE ) );
  const [ postClient, setPostClient ] = useState<PostClient>( structuredClone( DEFAULT_POST_CLIENT ) );
  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | undefined>();
  const [ selectedSite, setSelectedSite ] = useState<Option<Site> | undefined>();
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserLinked>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<Option<Site>[]>( [] );

  const [ rerenderClient, setRerenderClient ] = useState<number>( 0 );
  const [ rerenderSite, setRerenderSite ] = useState<number>( 0 );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );
  const [ needCreateNewSite, setNeedCreateNewSite ] = useState<boolean>( false );
  const [ needCreateNewClient, setNeedCreateNewClient ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me && !( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) ) {
      NavigationService.toSectors( navigate, false );
    }
    refreshClients();
  }, [] );

  useEffect( () => {
    checkCanPostClient();
  }, [ postSector, postSite, postClient, selectedClient, selectedSite ] );

  useEffect( () => {
    checkHasChangeClient();
  }, [ postSector, postSite, postClient, selectedClient, selectedSite, selectedUsers ] );

  useEffect( () => {
    setRerenderClient( rerenderClient + 1 );
  }, [ allowedClients ] );

  useEffect( () => {
    setRerenderSite( rerenderSite + 1 );
  }, [ allowedSites ] );

  useEffect( () => {
    if ( selectedClient != null ) {
      refreshSites( selectedClient.object.id );
    }
    postSite.clientId = selectedClient?.value || '';
    setNeedCreateNewClient( selectedClient?.object.id === createNewClient.id );
    setRerenderSite( rerenderSite + 1 );
  }, [ selectedClient ] );

  useEffect( () => {
    setSelectedUsers( [] );
    postSector.siteId = selectedSite?.value || '';
    setNeedCreateNewSite( selectedClient != undefined && selectedSite?.object.id === createNewSite.id );
    setPostSector( structuredClone( postSector ) );
  }, [ selectedSite ] );

  useEffect( () => {
    postSector.users = selectedUsers.filter( user => { return !user.object.isAdmin; } ).map( user => { return user.object.id; } );
    postSector.admins = selectedUsers.filter( user => { return user.object.isAdmin; } ).map( admin => { return admin.object.id; } );
    setPostSector( structuredClone( postSector ) );
  }, [ selectedUsers ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    if ( response != null ) {
      if ( me?.isSuperAdmin ) {
        response.unshift( createNewClient );
      }
      const clients = CommonGlobalService.toOptions( response )
        .sort( client => { return client.object.id < createNewClient.id ? 1 : -1; } );
      setAllowedClients( clients );
      if ( clients.length === 1 ) {
        setSelectedClient( clients[ 0 ] );
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const refreshSites = async ( clientId: string ): Promise<void> => {
    let response: Site[] = [];
    if ( clientId != null && clientId != '' && clientId != createNewClient.id ) {
      response = await SiteService.getAllowedSitesByClientsId( [ clientId ], token, dispatch );
    }
    const sitesAdmin = response.filter( site => {
      return ( me && ( CommonGlobalService.isAdminOfSite( me, site.id ) || CommonGlobalService.isAdminOfClient( me, site.clientId ) ) );
    } );
    if ( selectedSite?.object.id != clientId ) {
      setSelectedSite( undefined );
    }
    if ( sitesAdmin != null ) {
      if ( me?.isSuperAdmin ) {
        sitesAdmin.unshift( createNewSite );
      }

      const sites = CommonGlobalService.toOptions( sitesAdmin )
        .sort( site => { return site.object.id < createNewSite.id ? 1 : -1; } );
      setAllowedSites( sites );
      if ( sites.length === 1 ) {
        setSelectedSite( sites[ 0 ] );
      }
    }
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    const users: { total: number, list: Option<UserLinked>[] } = { total: 0, list: [] };
    if ( selectedSite && selectedSite.object.id !== createNewSite.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedClient != null && selectedSite != null ) {
        filter.filterClients = [ selectedClient.object.id ];
      }

      const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
      users.total = result.total;
      users.list = CommonGlobalService.toOptions<UserLinked>(
        result.users.map<UserLinked>( user => {
          return {
            id: user.id,
            fullname: user.fullname,
            isDisable: user.isDisable,
            isAdminOfUser: user.isAdminOfUser,
            isAdmin: false,
          };
        } )
      );
    }
    return users;
  };

  const resetForm = () => {
    setSelectedUsers( [] );
    if ( needCreateNewClient ) {
      refreshClients();
    }
    setPostClient( structuredClone( DEFAULT_POST_CLIENT ) );
    setPostSite( structuredClone( DEFAULT_POST_SITE ) );
    setPostSector( structuredClone( DEFAULT_POST_SECTOR ) );
    const defaultClient = allowedClients.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedClients[ 0 ] : undefined;
    const defaultSite = allowedSites.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedSites[ 0 ] : undefined;
    setSelectedClient( defaultClient );
    setSelectedSite( defaultSite );
  };

  const checkCanPostClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postSector.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
      ( postSector.siteId != createNewSite.id && postSector.siteId != null && postSector.siteId != '' )
    );
    const nameChange = postSector.name != null && postSector.name != '';
    setCanPost( clientChange && siteChange && nameChange );
  };

  const checkHasChangeClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
      ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postSector.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
      ( postSector.siteId != createNewSite.id && postSector.siteId != null && postSector.siteId != '' )
    );
    const nameChange = postSector.name != null && postSector.name != '';
    const usersChange = selectedUsers.length > 0;
    setHasChange( clientChange || siteChange || nameChange || usersChange );
  };

  /*------------------------------ Events -------------------------------*/
  const onCreateClick = async () => {
    setIsLoading( true );
    if ( postSite.clientId === createNewClient.id ) {
      const newClientId = await ClientService.postClient( postClient, token, dispatch );
      if ( newClientId ) {
        postSite.clientId = newClientId;
      }
    }
    if ( postSector.siteId === createNewSite.id ) {
      const newSiteId = await SiteService.postSite( postSite, token, dispatch );
      if ( newSiteId ) {
        postSector.siteId = newSiteId;
      }
    }
    if ( postSector.siteId !== createNewSite.id ) {
      const isCreated = await SectorService.postSector( postSector, token, dispatch );
      if ( isCreated ) {
        toastService.showSuccess( t( 'The {0} sector has been created.' ).replace( '{0}', ( postSector.name ) ) );
        resetForm();
      }
    }
    setIsLoading( false );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create sector' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:sector' ), route: ROUTES.SECTORS, icon: ( <SectorsIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new sector' ), route: ROUTES.NEW_SECTOR, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderClient }
                    disabled={ allowedClients.length === 1 }
                    value={ selectedClient }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ allowedClients }
                    onChange={ option => {
                      setSelectedClient( option );
                    } }
                  />
                  <div className='flex flex-col w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewClient ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                        label={ t( 'Client name' ) }
                        placeholder={ t( 'Client' ) + '...' }
                        icon={ <ClientsIcon className='w-7 h-7' /> }
                        value={ postClient.name }
                        onChange={ newClientName => {
                          postClient.name = newClientName;
                          setPostClient( structuredClone( postClient ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderSite }
                    disabled={ !selectedClient || allowedSites.length === 1 }
                    value={ selectedSite }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ allowedSites }
                    onChange={ option => {
                      setSelectedSite( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewSite ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                        label={ t( 'Site name' ) }
                        placeholder={ t( 'Site' ) + '...' }
                        icon={ <SitesIcon className='w-7 h-7' /> }
                        value={ postSite.name }
                        onChange={ newSiteName => {
                          postSite.name = newSiteName;
                          setPostSite( structuredClone( postSite ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_SECTOR_NAME }
                    label={ t( 'Sector name' ) }
                    placeholder={ t( 'Sector' ) + '...' }
                    icon={ <SectorsIcon className='w-7 h-7' /> }
                    value={ postSector.name }
                    onChange={ newSectorName => {
                      postSector.name = newSectorName;
                      setPostSector( structuredClone( postSector ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    key={ selectedSite?.value }
                    disabled={ !selectedSite || selectedSite.object.id === createNewSite.id }
                    label={ t( 'Users' ) }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ selectedValue => {
                      return (
                        <SwitchAdminUser
                          objectId={ selectedValue.value }
                          isDisabled={ selectedValue.isDisabled }
                          defaultValue={ selectedValue.object.isAdmin }
                          onChange={ value => {
                            selectedValue.object.isAdmin = value;
                            const findedUser = selectedUsers.find( item => { return selectedValue.object.id == item.value; } );
                            if ( findedUser ) {
                              findedUser.object.isAdmin = value;
                            }
                            setSelectedUsers( structuredClone( selectedUsers ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}